<template>
  <div class="card">
    <div class="card-body">
      <div class="d-flex justify-content-between">
        <h4>
          <small class="font-weight-bold">{{ $t('dashboard.filters.label') }}</small>
        </h4>
        <div>
          <img
            v-if="isExpand"
            class="pointer"
            src="@/assets/icon/up-arrow.png"
            alt="polygon"
            data-toggle="collapse"
            data-target="#collapseFilterChart"
            @click="isExpand = !isExpand"
          >
          <img
            v-if="!isExpand"
            class="pointer"
            src="@/assets/icon/down-arrow.png"
            alt="polygon"
            data-toggle="collapse"
            data-target="#collapseFilterChart"
            @click="isExpand = !isExpand"
          >
        </div>
      </div>
      <div
        id="collapseFilterChart"
        class="collapse"
      >
        <div class="row my-3">
          <div class="col-sm-3">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('dashboard.filters.period-label') }}</small>
            </h6>
            <v-select
              v-model="filters.period"
              class="sm"
              :get-option-label="getPeriodLabel"
              :clearable="false"
              :options="periodList"
              :reduce="getReduceId"
            />
          </div>
          <div class="col-sm-4">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('dashboard.filters.date-label') }}</small>
            </h6>
            <div>
              <date-picker
                v-model="dateTime"
                class="col-12 p-0"
                :type="filters.period === 'daily' ? 'date' : 'month'"
                :disabled-date="disableDate"
                range
                placeholder="Select date range"
                @pick="updateChangeDate"
              />
            </div>
          </div>
        </div>
        <div class="row my-3">
          <div class="col-sm-3">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('dashboard.filters.employee-label') }}</small>
            </h6>
            <v-select
              v-model="employeeType"
              class="sm"
              :get-option-label="getEmployeeLabel"
              multiple
              :placeholder="$t('dashboard.filters.employee-placeholder')"
              :clearable="false"
              :options="employeeList"
              :reduce="getReduceId"
            />
          </div>
          <div class="col-sm-3">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('filter-assessment.risk-level') }}</small>
            </h6>
            <v-select
              v-model="filters.risk"
              class="sm"
              :get-option-label="getRiskLevelLabel"
              multiple
              :placeholder="$t('filter-assessment.placeholder.risk-level')"
              :clearable="false"
              :options="riskLevels"
              :reduce="getReduceName"
            />
          </div>
          <div class="col-sm-3">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('filter-assessment.organization') }}</small>
            </h6>
            <v-select
              v-model="filters.organization"
              class="sm"
              label="name"
              multiple
              :placeholder="$t('filter-assessment.placeholder.organization')"
              :options="workLocations"
              :reduce="getReduceName"
              :selectable="getOrganizationSelectable"
            >
              <template #option="{ group, name }">
                <div v-if="group">
                  {{ group }}
                </div>
                {{ name }}
              </template>
            </v-select>
          </div>
          <div class="col-sm-3">
            <h6 class="mb-1">
              <small class="font-weight-bold">{{ $t('dashboard.filters.work-location-label') }}</small>
            </h6>
            <v-select
              v-model="filters.workLocation"
              class="sm"
              label="name"
              multiple
              :placeholder="$t('dashboard.filters.work-location-placeholder')"
              :options="workLocationsActual"
              :reduce="getReduceName"
              :selectable="option => !option.hasOwnProperty('group')"
            >
              <template #option="{ group, name }">
                <div v-if="group">
                  {{ group }}
                </div>
                {{ name }}
              </template>
            </v-select>
          </div>
        </div>
        <div class="mt-3 d-flex justify-content-end">
          <button
            class="btn btn-info btn-sm font-weight-bold mr-2"
            type="button"
            @click="applyFilter"
          >
            {{ $t("filter-assessment.apply") }}
          </button>
          <div
            class="btn btn-outline-info btn-sm font-weight-bold"
            type="button"
            @click="resetFilter"
          >
            {{ $t("filter-assessment.clear") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sub, differenceInDays } from 'date-fns'
import VSelect from 'vue-select'
import DatePicker from 'vue2-datepicker'
import { PERIOD_TYPE, EMPLOYEE_TYPE, RISK_LEVELS } from '@/fixtures'
import Api from '@/utils/Api'

export default {
  components: {
    VSelect,
    DatePicker
  },

  props: {
    filters: {
      type: Object,
      default: () => ({
        period: 'daily',
        startDate: sub(new Date(), { days: 30 }),
        endDate: new Date()
      })
    },
    isApply: {
      type: Boolean,
      default: () => (false)
    }
  },

  data () {
    return {
      user: JSON.parse(localStorage.getItem('profile')) || {},
      dateTime: [this.filters.startDate, this.filters.endDate],
      employeeType: [],
      workLocations: [],
      workLocationsActual: [],
      mode: 0,
      isExpand: false
    }
  },

  computed: {
    periodList: () => PERIOD_TYPE,
    employeeList: () => EMPLOYEE_TYPE,
    riskLevels: () => RISK_LEVELS
  },

  watch: {
    dateTime () {
      this.$emit('updateDate', this.dateTime)
    },
    employeeType () {
      this.$emit('updateEmployee', this.employeeType)
    },
    filters () {
      this.dateTime = [this.filters.startDate, this.filters.endDate]
    }
  },

  mounted () {
    this.fetchWorkLocations()
    this.getWorkLocationsActual()
  },

  methods: {
    async fetchWorkLocations () {
      const { data } = await Api.masterData.customWorkLocations({
        Filters: 'IsOrganization==true'
      })
      this.workLocations = data.data.reduce((acc, curr) => {
        const newGroup = (!acc.find(a => a.group === curr.category) &&
          [{ group: curr.category, name: null }]) ||
          []
        return [
          ...acc,
          ...newGroup,
          curr
        ]
      }, [])
    },
    async getWorkLocationsActual () {
      const { data } = await Api.masterData.workLocations({
        Filters: `companyId==${this.user.company ? this.user.company.id : ''}`
      })
      this.workLocationsActual = data.data.reduce((acc, curr) => {
        const newGroup = (!acc.find(a => a.group === curr.category) &&
          [{ group: curr.category, name: null }]) ||
          []
        return [
          ...acc,
          ...newGroup,
          curr
        ]
      }, [])
    },
    getReduceId: ({ id }) => id,
    getReduceName: ({ name }) => name,
    getOrganizationSelectable: ({ group }) => !group,
    disableDate (date) {
      switch (this.mode) {
        case 1: {
          const today = new Date()
          const difference = differenceInDays(today, this.dateTime[0])
          const selectedDate = new Date()
          selectedDate.setDate(selectedDate.getDate() - difference)

          if (this.filters.period === 'daily') {
            return date > new Date() || date > new Date(selectedDate.getTime() + 31 * 24 * 3600 * 1000) || date < new Date(selectedDate.getTime() - 31 * 24 * 3600 * 1000)
          }
          if (this.filters.period === 'monthly') {
            return date > new Date() || date > new Date(selectedDate.getTime() + 365 * 24 * 3600 * 1000) || date < new Date(selectedDate.getTime() - 365 * 24 * 3600 * 1000)
          }
          break
        }
        default: {
          return date > new Date()
        }
      }
    },
    updateChangeDate (date) {
      this.mode++
      if (this.mode === 1) {
        this.dateTime[0] = date
      }
      if (this.mode === 2) {
        this.mode = 0
      }
    },
    applyFilter () {
      this.$emit('updateApply', false)
    },
    resetFilter () {
      this.employeeType = []
      this.$emit('updateApply', true)
    },
    getPeriodLabel ({ name }) {
      return this.$t(`dashboard.filters.period-dropdown.${name}`)
    },
    getEmployeeLabel ({ name }) {
      return this.$t(`dashboard.filters.employee-dropdown.${name}`)
    },
    getRiskLevelLabel ({ name }) {
      return this.$t(`filter-assessment.risk-levels.${name}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}

/deep/ .vs__dropdown-toggle {
  background: white;
}

/deep/ .vs__selected-options {
  flex-wrap: nowrap;

  .vs--open & {
    flex-wrap: wrap;
  }
}
</style>
